import React, { useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import { setStoredGeo } from "../lib/geo";
import { useSeoMeta, SEO_BASE_URL } from "../lib/useSeoMeta";
import Home from "./Home";

/**
 * /c/:country — Country-targeted landing pages for SEO.
 *
 * Distinct URLs per country (subdirectories) earn local Google rankings.
 * This route persists the chosen country in localStorage so the Home
 * page underneath auto-filters to it, then injects per-country
 * canonical / OG / description / JSON-LD via `useSeoMeta`.
 */

const SUPPORTED = new Set([
    "us", "in", "gb", "ca", "au", "ae", "sa", "sg", "my", "de", "fr", "it", "es",
    "nl", "jp", "cn", "br", "mx", "za", "ng", "ke", "eg", "pk", "bd", "lk", "np",
    "id", "ph", "th", "vn", "tr", "ru", "ua", "pl", "se", "no", "dk", "fi", "ie",
    "ch", "at", "be", "pt", "gr", "cz",
]);

const NAMES = {
    us: "United States", in: "India", gb: "United Kingdom", ca: "Canada",
    au: "Australia", ae: "United Arab Emirates", sa: "Saudi Arabia",
    sg: "Singapore", my: "Malaysia", de: "Germany", fr: "France", it: "Italy",
    es: "Spain", nl: "Netherlands", jp: "Japan", cn: "China", br: "Brazil",
    mx: "Mexico", za: "South Africa", ng: "Nigeria", ke: "Kenya", eg: "Egypt",
    pk: "Pakistan", bd: "Bangladesh", lk: "Sri Lanka", np: "Nepal",
    id: "Indonesia", ph: "Philippines", th: "Thailand", vn: "Vietnam",
    tr: "Türkiye", ru: "Russia", ua: "Ukraine", pl: "Poland", se: "Sweden",
    no: "Norway", dk: "Denmark", fi: "Finland", ie: "Ireland",
    ch: "Switzerland", at: "Austria", be: "Belgium", pt: "Portugal",
    gr: "Greece", cz: "Czechia",
};

const CURRENCIES = {
    us: "USD", in: "INR", gb: "GBP", ca: "CAD", au: "AUD", ae: "AED", sa: "SAR",
    sg: "SGD", my: "MYR", de: "EUR", fr: "EUR", it: "EUR", es: "EUR", nl: "EUR",
    jp: "JPY", cn: "CNY", br: "BRL", mx: "MXN", za: "ZAR", ng: "NGN", ke: "KES",
    eg: "EGP", pk: "PKR", bd: "BDT", lk: "LKR", np: "NPR", id: "IDR", ph: "PHP",
    th: "THB", vn: "VND", tr: "TRY", ru: "RUB", ua: "UAH", pl: "PLN", se: "SEK",
    no: "NOK", dk: "DKK", fi: "EUR", ie: "EUR", ch: "CHF", at: "EUR", be: "EUR",
    pt: "EUR", gr: "EUR", cz: "CZK",
};

// v78.55.1 — Canonicalise common full-name slugs (e.g. /c/india,
// /c/united-states, /c/Türkiye) to their ISO-2 codes BEFORE deciding to
// redirect. Crawlers and humans both hit these long-form URLs from external
// links — pre-v78.55 they silently landed on Home; with Home now gated, an
// unrecognised slug would bounce anonymous users to /auth and break SEO.
const NAME_TO_ISO = {
    "united states": "us", "united-states": "us", "usa": "us", "america": "us",
    "india": "in", "bharat": "in",
    "united kingdom": "gb", "united-kingdom": "gb", "uk": "gb", "britain": "gb", "england": "gb",
    "canada": "ca", "australia": "au", "uae": "ae", "united arab emirates": "ae",
    "united-arab-emirates": "ae", "saudi arabia": "sa", "saudi-arabia": "sa",
    "ksa": "sa", "singapore": "sg", "malaysia": "my", "germany": "de", "deutschland": "de",
    "france": "fr", "italy": "it", "italia": "it", "spain": "es", "españa": "es",
    "netherlands": "nl", "holland": "nl", "japan": "jp", "china": "cn", "brazil": "br",
    "brasil": "br", "mexico": "mx", "south africa": "za", "south-africa": "za",
    "nigeria": "ng", "kenya": "ke", "egypt": "eg", "pakistan": "pk", "bangladesh": "bd",
    "sri lanka": "lk", "sri-lanka": "lk", "nepal": "np", "indonesia": "id",
    "philippines": "ph", "thailand": "th", "vietnam": "vn", "viet nam": "vn",
    "turkey": "tr", "türkiye": "tr", "turkiye": "tr", "russia": "ru", "ukraine": "ua",
    "poland": "pl", "sweden": "se", "norway": "no", "denmark": "dk", "finland": "fi",
    "ireland": "ie", "switzerland": "ch", "austria": "at", "belgium": "be",
    "portugal": "pt", "greece": "gr", "czechia": "cz", "czech republic": "cz",
    "czech-republic": "cz",
};

function canonicaliseCountrySlug(raw) {
    if (!raw) return "";
    const s = raw.toLowerCase().trim();
    if (SUPPORTED.has(s)) return s;
    if (NAME_TO_ISO[s]) return NAME_TO_ISO[s];
    // Try decoded variant for /c/türkiye style URLs that arrive percent-encoded.
    try {
        const decoded = decodeURIComponent(s);
        if (SUPPORTED.has(decoded)) return decoded;
        if (NAME_TO_ISO[decoded]) return NAME_TO_ISO[decoded];
    } catch { /* ignore malformed escapes */ }
    return "";
}

export default function CountryLanding() {
    const { country } = useParams();
    // v78.55.1 — Canonicalise full-name slugs (india / united-states / türkiye)
    // to ISO-2 so /c/india etc. don't redirect into the now-gated `/`.
    const cc = canonicaliseCountrySlug(country);
    const valid = !!cc;
    const name = valid ? (NAMES[cc] || cc.toUpperCase()) : "";

    // Persist the picked country so the Home feed underneath auto-filters.
    useEffect(() => {
        if (!valid) return;
        setStoredGeo(cc.toUpperCase(), CURRENCIES[cc] || "USD", { country_name: name });
    }, [cc, valid, name]);

    useSeoMeta(valid ? {
        title: `Adukoo ${name} — Buy & Sell locally on the worldwide marketplace`,
        description: `Browse listings across ${name} on Adukoo — used cars, real estate, jobs, electronics, fashion and verified businesses. Local sellers, global reach, instant chat.`,
        canonical: `${SEO_BASE_URL}/c/${cc}`,
        type: "website",
        jsonLd: {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": `Adukoo ${name}`,
            "url": `${SEO_BASE_URL}/c/${cc}`,
            "isPartOf": { "@type": "WebSite", "@id": `${SEO_BASE_URL}/#website` },
            "about": { "@type": "Country", "name": name },
            "breadcrumb": {
                "@type": "BreadcrumbList",
                "itemListElement": [
                    { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SEO_BASE_URL}/` },
                    { "@type": "ListItem", "position": 2, "name": name, "item": `${SEO_BASE_URL}/c/${cc}` },
                ],
            },
        },
    } : { title: "Adukoo — Worldwide marketplace" });

    if (!valid) {
        // v78.55.1 — Was `<Navigate to="/" replace />` which now bounces
        // anonymous visitors to /auth (Home is gated). Send unrecognised
        // slugs to the public /browse instead so crawlers + humans land
        // somewhere indexable.
        return <Navigate to="/browse" replace />;
    }
    // Re-use the Home page — geo-picker reads from localStorage, so the
    // listing feed will auto-filter to the active country. Home detects
    // it's on a /c/{country} path and yields the SEO tags to us.
    return <Home />;
}
